import { RouterLink } from '@redux-first-router-link';
import throttle from 'lodash/throttle';
import { useCallback, useMemo } from 'react';
import { useTracking } from 'react-tracking';

import { Grid, Text } from '@simplywallst/ui-core';

import { Snowflake } from '@simplywallst/ui-charts/Snowflake';

import { CompanyIntrinsicDiscount } from '@/components/CompanyIntrinsicDiscount';
import MiniPriceChart, { DeferRender } from '@/components/MiniPriceChart';
import PositiveNegativeValue from '@/components/PositiveNegativeValue';

import { formatterCurrency, formatterPercentage } from '@/utilities/format';

import { useNormalisedCompanySnowflakeScore } from '@/hooks/useNormalisedCompanySnowflakeScore';
import { getTradingItemCurrencyISO } from '@/utilities/getTradingItemCurrencyISO';

import {
  formatCurrencyToScaleTruncated,
  formatCurrencyTruncatedWithSign,
} from '@/utilities/portfolio/formatValue';

import { TableRowCell } from '../../Table';

import { HoldingsTableRow } from './styled';
import type { RowProps } from './types';

export const DemoHoldingRow = ({
  index,
  holding,
  selected,
  onHoldingSelect,
  'data-cy-id': dataCyId,
}: RowProps) => {
  const { id, company, analysis, currencyISO } = holding;

  const lastSharePrice = company.analysisValue?.lastSharePrice ?? null;
  const npvPerShare = company.analysisValue?.npvPerShare ?? null;

  const companyCurrencyISO =
    getTradingItemCurrencyISO(holding.company.data.currencyInfo) ?? 'XXX';

  const { Track, trackEvent } = useTracking({
    modifier: 'desktopRow',
  });

  const points = useNormalisedCompanySnowflakeScore(company.score);

  const [handleMouseEnter, handleMouseLeave] = useMemo(() => {
    const throttleHover = throttle(() => {
      onHoldingSelect(id);
      trackEvent({
        index,
        action: 'hover',
        canonicalUrl: company.canonicalURL,
      });
    }, 300);

    const cancelThrottleHover = () => {
      throttleHover.cancel();
    };
    return [throttleHover, cancelThrottleHover];
  }, [onHoldingSelect, company.canonicalURL, trackEvent, id, index]);

  const handleTrackingLink = useCallback(() => {
    trackEvent({
      index,
      action: 'click',
      canonicalUrl: company.canonicalURL,
    });
  }, [trackEvent, company.canonicalURL, index]);

  const handleClick = useCallback(() => {
    onHoldingSelect(null);
    trackEvent({
      action: 'click',
    });
  }, [onHoldingSelect, trackEvent]);

  return (
    <Track>
      <HoldingsTableRow
        selected={selected}
        data-cy-id={dataCyId}
        data-testid={`holdings-table-row-${id}`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        tabIndex={0}
      >
        <TableRowCell>
          <Grid
            data-cy-id="company-column"
            templateColumns="48px auto"
            alignItems="center"
            gap="8px"
            paddingRight="x1"
            onClick={handleClick}
          >
            <Snowflake width={48} points={points}>
              <Snowflake.DotMarkers />
            </Snowflake>
            <div>
              <RouterLink
                data-cy-id="ticker-symbol"
                noUnderline
                to={company.canonicalURL}
                onClick={handleTrackingLink}
              >
                <strong>{company.tickerSymbol}</strong>
              </RouterLink>
              <Text
                typography="xSmall"
                color="whiteRGBA"
                opacity={0.5}
                lineClamp={1}
              >
                {company.name}
              </Text>
            </div>
          </Grid>
        </TableRowCell>
        <TableRowCell>
          <div onClick={handleClick} data-cy-id="share-price">
            <Text typography="xSmall" isTruncated>
              {formatterCurrency(lastSharePrice, companyCurrencyISO)}
            </Text>
            <Text typography="tinyCondensed" color="whiteRGBA" opacity={0.5}>
              <CompanyIntrinsicDiscount
                lastSharePrice={lastSharePrice}
                npvPerShare={npvPerShare}
                fairValue={analysis.fairValue}
              />
            </Text>
          </div>
        </TableRowCell>
        <TableRowCell>
          <div onClick={handleClick} data-cy-id="return-7d">
            <PositiveNegativeValue value={analysis.return7d}>
              {formatterPercentage(analysis.return7d)}
            </PositiveNegativeValue>
            <Text typography="tinyCondensed" color="whiteRGBA" opacity={0.5}>
              {formatCurrencyTruncatedWithSign(
                analysis.return7dValue,
                currencyISO
              )}
            </Text>
          </div>
        </TableRowCell>
        <TableRowCell>
          <div onClick={handleClick} data-cy-id="holding-roi">
            <PositiveNegativeValue value={analysis.roi}>
              {formatterPercentage(analysis.roi)}
            </PositiveNegativeValue>
            <Text typography="tinyCondensed" color="whiteRGBA" opacity={0.5}>
              {formatCurrencyTruncatedWithSign(analysis.roiValue, currencyISO)}
            </Text>
          </div>
        </TableRowCell>
        <TableRowCell>
          <div onClick={handleClick} data-cy-id="holding-value">
            <Text typography="xSmall" isTruncated>
              {formatCurrencyToScaleTruncated(analysis.value, currencyISO)}
            </Text>
            <Text typography="tinyCondensed" color="whiteRGBA" opacity={0.5}>
              {formatCurrencyToScaleTruncated(
                analysis.capitalInvested,
                currencyISO
              )}
            </Text>
          </div>
        </TableRowCell>
        <TableRowCell colSpan={2}>
          <DeferRender>
            <MiniPriceChart
              period="1Y"
              companyId={company.id}
              currencyISO={companyCurrencyISO}
            />
          </DeferRender>
        </TableRowCell>
      </HoldingsTableRow>
    </Track>
  );
};
